<template>
  <div class="v2026-stab-tabs">
    <!-- The chevrons sit OUTSIDE role=tablist so a screen reader never announces them as tabs. -->
    <button v-if="overflowing"
            type="button"
            class="v2026-stab-tabs__nav"
            :disabled="!canScrollPrev"
            aria-label="เลื่อนแท็บไปทางซ้าย"
            @click="scrollRail(-1)">
      <span class="v2026-stab-tabs__nav-ico" aria-hidden="true" :style="maskStyle(chevronLeft)"></span>
    </button>

    <div ref="scroller"
         class="v2026-stab-tabs__scroll"
         role="tablist"
         :aria-label="ariaLabel"
         aria-orientation="horizontal"
         @scroll.passive="measure"
         @keydown="onKeydown">
      <button v-for="tab in tabs"
              :key="tab.type"
              ref="tabBtns"
              type="button"
              role="tab"
              :id="idPrefix + tab.type"
              class="v2026-stab-tab"
              :class="{ 'is-active': isActive(tab.type) }"
              :aria-selected="isActive(tab.type) ? 'true' : 'false'"
              :tabindex="isActive(tab.type) ? 0 : -1"
              :aria-controls="panelId || null"
              @click="$emit('select', tab.type)">
        <span class="v2026-stab-tab__ico" aria-hidden="true" :style="maskStyle(tab.icon)"></span>{{ tab.label }}</button>
    </div>

    <button v-if="overflowing"
            type="button"
            class="v2026-stab-tabs__nav"
            :disabled="!canScrollNext"
            aria-label="เลื่อนแท็บไปทางขวา"
            @click="scrollRail(1)">
      <span class="v2026-stab-tabs__nav-ico" aria-hidden="true" :style="maskStyle(chevronRight)"></span>
    </button>
  </div>
</template>

<script>
const CHEVRON_LEFT = require('@/assets/icons2026/chevron-left.svg')
const CHEVRON_RIGHT = require('@/assets/icons2026/chevron-right.svg')

export default {
  name: 'settings-tabs-v2026',
  props: {
    // Required, with no default: every page states its own labels (constants/settingsTabs.js).
    tabs: {
      type: Array,
      required: true
    },
    activeType: {
      type: String,
      default: ''
    },
    ariaLabel: {
      type: String,
      default: 'ประเภท'
    },
    // The consumer's tabpanel repeats this prefix in its aria-labelledby. The two strings live in
    // two files: change one without the other and the panel silently loses its accessible name.
    idPrefix: {
      type: String,
      default: 'v2026-stab-'
    },
    panelId: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      chevronLeft: CHEVRON_LEFT,
      chevronRight: CHEVRON_RIGHT,
      overflowing: false,
      canScrollPrev: false,
      canScrollNext: false
    }
  },
  computed: {
    activeIndex () {
      const i = this.tabs.findIndex(tab => tab.type === this.activeType)
      return i === -1 ? 0 : i
    }
  },
  watch: {
    activeType () {
      this.$nextTick(this.scrollActiveIntoView)
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.measure()
      this.scrollActiveIntoView()
    })
    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(this.measure)
      this._ro.observe(this.$refs.scroller)
    }
    window.addEventListener('resize', this.measure)
  },
  beforeDestroy () {
    if (this._ro) {
      this._ro.disconnect()
      this._ro = null
    }
    window.removeEventListener('resize', this.measure)
  },
  methods: {
    isActive (type) {
      return this.activeType === type
    },
    maskStyle (url) {
      return {
        '-webkit-mask-image': 'url(' + url + ')',
        'mask-image': 'url(' + url + ')'
      }
    },
    measure () {
      const el = this.$refs.scroller
      if (!el) return
      this.overflowing = el.scrollWidth > el.clientWidth + 1
      this.canScrollPrev = el.scrollLeft > 0
      this.canScrollNext = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
    },
    scrollRail (dir) {
      const el = this.$refs.scroller
      if (!el) return
      el.scrollLeft += dir * el.clientWidth * 0.8
    },
    scrollActiveIntoView () {
      const btns = this.$refs.tabBtns || []
      const btn = btns[this.activeIndex]
      if (btn) btn.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    },
    // Manual activation: arrows move focus only. Automatic activation would fire one GET per
    // arrow press — eight requests to cross the rail, on every page that mounts this rail.
    onKeydown (e) {
      const btns = this.$refs.tabBtns || []
      if (!btns.length) return
      const current = btns.indexOf(document.activeElement)
      const from = current === -1 ? this.activeIndex : current
      let next = null
      if (e.key === 'ArrowRight') next = (from + 1) % btns.length
      else if (e.key === 'ArrowLeft') next = (from - 1 + btns.length) % btns.length
      else if (e.key === 'Home') next = 0
      else if (e.key === 'End') next = btns.length - 1
      if (next === null) return
      e.preventDefault()
      btns[next].focus()
      btns[next].scrollIntoView({ block: 'nearest', inline: 'nearest' })
    }
  }
}
</script>

<style lang="scss" scoped>
.v2026-stab-tabs {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 48px;
  border-bottom: 1px solid #e9ecef;
  margin-top: 4px;
}

.v2026-stab-tabs__scroll {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: flex-end;
  gap: 4px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  scrollbar-width: none;

  &::-webkit-scrollbar { display: none; }
}

.v2026-stab-tab {
  position: relative;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 12px;
  border: 0;
  background: none;
  border-radius: 8px 8px 0 0;
  font-size: 13px;
  line-height: 1;
  color: #343a40;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 150ms ease;

  &:hover:not(.is-active) { background: var(--v2026-color-hover-bg); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }

  &.is-active {
    background: var(--v2026-color-main-soft);
    color: var(--v2026-color-main-icon);
    font-weight: 600;

    /* Non-colour signal: the accent-on-tint pair is ~2.3:1, so colour never carries state alone. */
    &::after {
      content: '';
      position: absolute;
      left: 10px;
      right: 10px;
      bottom: 0;
      height: 2px;
      border-radius: 2px 2px 0 0;
      background: var(--v2026-color-main-bar);
    }

    .v2026-stab-tab__ico { background-color: var(--v2026-color-main-icon); }
  }
}

.v2026-stab-tab__ico {
  flex: 0 0 auto;
  display: inline-block;
  width: 16px;
  height: 16px;
  background-color: var(--v2026-color-icon-idle);
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
}

.v2026-stab-tabs__nav {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  margin-bottom: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 6px;
  background: none;
  cursor: pointer;

  &:hover:not(:disabled) { background: var(--v2026-color-hover-bg); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
  &:disabled { opacity: .35; cursor: default; }
}

.v2026-stab-tabs__nav-ico {
  display: inline-block;
  width: 16px;
  height: 16px;
  background-color: var(--v2026-color-icon-idle);
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
}

@media (prefers-reduced-motion: reduce) {
  .v2026-stab-tab { transition: none; }
  .v2026-stab-tabs__scroll { scroll-behavior: auto; }
}
</style>
